import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

// Components
import KogModalSideComponent from '../../modal/ModalSide';
import KogModalSlideImages from '../KogModalSlideImages/modalSlideImages';
import LoadingComponent from '../loading/loadingComponent';
import CollapseFiles from './collapseFiles';
import CollapseImageCategory from './collapseImageCategory';

// Utils
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import { downloadFile, convertImageToBase64 } from '@/main/services/utilsFile';

// Api's
import { quoteApi, quoteImageApi } from '@/main/api';

// Models
import { IKogModalProps } from '@/main/@types/interfaceIcons';
import { QuoteImageResponse } from '@/main/models/response';
import { SaveQuoteImagesRequest } from '@/main/models/request';

// Styles
import * as S from './styles';
import { Button } from '@mui/material';
import { useLocation } from 'react-router-dom';
import QuoteImageUploader from '../quoteImageUploader/quoteImageUploader';

type ActiveCollapseType = 'files' | 'initial' | 'supplementary' | null;

interface ModalImagesProps extends IKogModalProps {
  canUploadImages: boolean;
  canShowPDF: boolean;
  canDownloadImages?: boolean;
  guidQuote: string;
  guidClaim?: string;
  removeDeleteButton?: boolean;
  showButtonImagesNewTab?: boolean;
}

function ModalImages({
  canUploadImages,
  canShowPDF,
  canDownloadImages = false,
  guidQuote,
  guidClaim,
  removeDeleteButton = false,
  showButtonImagesNewTab = false
}: ModalImagesProps) {
  const classes = S.useStyles();

  // States
  const [imageIndex, setImageIndex] = useState(0);
  const [isOpenModalFade, setIsOpenModalFade] = useState<boolean>(false);
  const [imagesSlide, setImagesSlide] = useState<string[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [activePanel, setActivePanel] = useState<ActiveCollapseType>(null);
  const [isLoadingSuppCheck, setIsLoadingSuppCheck] = useState<boolean>(true);
  const [hasSupplementary, setHasSupplementary] = useState<boolean>(false);
  const sliderCategoryRef = useRef<'initial' | 'supplementary'>('initial');
  const [hasNoMoreImages, setHasNoMoreImages] = useState(false);
  const imageMapRef = useRef<Record<string, QuoteImageResponse>>({});
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [isSavingImage, setIsSavingImage] = useState<boolean>(false);
  const location = useLocation();

  const slidePageRef = useRef(0);

  const hasMoreImages = imagesSlide.length < totalRecords && !hasNoMoreImages;

  const handleToggleCollapse = (
    panel: ActiveCollapseType,
    isExpanded: boolean
  ) => {
    if (isExpanded) {
      setActivePanel(panel);
    } else {
      setActivePanel(prevPanel => (prevPanel === panel ? null : prevPanel));
    }
  };

  const handleLoadMoreSlideImages = async (): Promise<string[]> => {
    slidePageRef.current += 1;
    const request = {
      guidQuote,
      guidClaim: guidClaim ?? null,
      pageIndex: slidePageRef.current,
      pageSize: PaginationEnum.PageSize10,
      includeInitialQuoteImages: sliderCategoryRef.current === 'initial',
      includeComplementQuoteImages:
        sliderCategoryRef.current === 'supplementary'
    };
    const response = await fetchDataHistoryData(request);

    const newImages = (response.results || []).map(
      (item: QuoteImageResponse) => {
        imageMapRef.current[item.externalKey] = item;
        return item.externalKey;
      }
    );

    if (newImages.length === 0) {
      setHasNoMoreImages(true);
    }

    return newImages;
  };

  useEffect(() => {
    setIsLoadingSuppCheck(true);
    quoteApi
      .getAllClaimImages({
        guidQuote,
        guidClaim: guidClaim ?? '',
        pageIndex: PaginationEnum.PageIndexIntial,
        pageSize: 1,
        includeInitialQuoteImages: false,
        includeComplementQuoteImages: true
      })
      .then(response => {
        const hasSupp =
          !!response.data.results && response.data.results.length > 0;
        setHasSupplementary(hasSupp);
        setActivePanel(hasSupp ? 'supplementary' : 'initial');
      })
      .finally(() => {
        setIsLoadingSuppCheck(false);
      });
  }, [guidQuote, guidClaim]);

  function fetchDataHistoryData(request: any) {
    return quoteApi.getAllClaimImages(request).then(response => response.data);
  }

  function reloadImages() {
    setImagesSlide([]);
    setTotalRecords(0);
    setReloadTrigger(prev => prev + 1);
  }

  function handleClickPreview(
    index: number,
    data: QuoteImageResponse[],
    total: number,
    category: 'initial' | 'supplementary'
  ) {
    data.forEach(item => {
      imageMapRef.current[item.externalKey] = item;
    });

    const imagesString = data.map(
      (item: QuoteImageResponse) => item.externalKey
    );
    setImagesSlide(imagesString);
    setTotalRecords(total);
    sliderCategoryRef.current = category;
    setHasNoMoreImages(false);

    slidePageRef.current = Math.ceil(data.length / PaginationEnum.PageSize10);

    setImageIndex(index);
    setIsOpenModalFade(true);
  }

  function handDeleteImage(guid: string) {
    if (!isNullUndefined(guid)) {
      quoteApi.deleteImageQuote(guid).then(() => {
        //reload because it affects ordering
        reloadImages();
        toast.success(
          getTranslateByKey('information.imagesDeletedSuccessfully')
        );
      });
    }
  }

  async function handleDownloadImage(
    imageUrl: string,
    passedImageRecord?: QuoteImageResponse
  ) {
    try {
      const imageRecord = passedImageRecord || imageMapRef.current[imageUrl];

      if (!imageRecord || !imageRecord.guid) {
        toast.error(getTranslateByKey('information.AnErrorOccurred', true));
        return;
      }

      const guidFile = imageRecord.guid;

      const response = await quoteImageApi.getDownloadImage(guidFile);
      let base64Data = response.data.result.value;

      if (!base64Data) {
        toast.error(getTranslateByKey('information.AnErrorOccurred', true));
        return;
      }

      if (base64Data.includes('base64,')) {
        base64Data = base64Data.split('base64,')[1];
      }

      const fileName =
        imageRecord.fileName || `image-${guidFile.substring(0, 8)}.jpeg`;

      downloadFile(
        base64Data,
        fileName,
        `image/${imageRecord.fileExtension.toLowerCase()}`
      );
    } catch (error) {
      console.error(error);
    }
  }

  //#endregion
  return (
    <S.Wrapper>
      <S.BoxModalImages>
        {!isLoadingSuppCheck ? (
          <>
            {isSavingImage ? (
              <LoadingComponent />
            ) : (
              <>
                {canUploadImages && (
                  <QuoteImageUploader
                    quoteImages={[]}
                    isFullScreenDragDrop
                    onCustomInsert={async newFiles => {
                      const base64ImagesArray = await Promise.all(
                        newFiles.map(image => convertImageToBase64(image))
                      );

                      const base64StringsArray = base64ImagesArray
                        .filter(Boolean)
                        .map(image => {
                          return {
                            imageUpload: `data:image/jpeg;base64,${image.base64Image}`,
                            imageName: image.name
                          };
                        });
                      setIsSavingImage(true);
                      const request: SaveQuoteImagesRequest = {
                        guidQuote: String(guidQuote),
                        imagesRequest: base64StringsArray
                      };
                      if (request.imagesRequest.length > 0) {
                        quoteApi
                          .saveQuoteImages(request)
                          .then(() => {
                            //reload because it affects ordering
                            reloadImages();
                            toast.success(
                              getTranslateByKey(
                                'information.imagesSavedSuccessfully'
                              )
                            );
                          })
                          .finally(() => setIsSavingImage(false));
                      }
                    }}
                  />
                )}
                {showButtonImagesNewTab && (
                  <Button
                    sx={{ textTransform: 'none', marginBottom: '16px' }}
                    onClick={() => {
                      window.open(
                        `/#${location.pathname}${location.search}&imageMode=true`,
                        '_blank'
                      );
                    }}>
                    {getTranslateByKey('information.openInNewTab')}
                  </Button>
                )}
              </>
            )}
            {canShowPDF && (
              <CollapseFiles
                guidQuote={guidQuote}
                guidClaim={guidClaim}
                expanded={activePanel === 'files'}
                onToggle={isExpanded =>
                  handleToggleCollapse('files', isExpanded)
                }
                reloadTrigger={reloadTrigger}
              />
            )}

            <CollapseImageCategory
              title={getTranslateByKey('information.initialImages')}
              categoryType="initial"
              guidQuote={guidQuote}
              guidClaim={guidClaim}
              expanded={activePanel === 'initial'}
              onToggle={isExpanded =>
                handleToggleCollapse('initial', isExpanded)
              }
              handleDownloadImage={handleDownloadImage}
              canDownloadImages={canDownloadImages}
              onPreview={(idx, data, total) =>
                handleClickPreview(idx, data, total, 'initial')
              }
              collapseMaxHeight="100%"
              collapseOverflow="hidden"
              removeDeleteButton={removeDeleteButton}
              handDeleteImage={handDeleteImage}
              reloadTrigger={reloadTrigger}
            />

            {hasSupplementary && (
              <CollapseImageCategory
                title={getTranslateByKey('information.supplementaryImages')}
                categoryType="supplementary"
                guidQuote={guidQuote}
                guidClaim={guidClaim}
                expanded={activePanel === 'supplementary'}
                onToggle={isExpanded =>
                  handleToggleCollapse('supplementary', isExpanded)
                }
                handleDownloadImage={handleDownloadImage}
                canDownloadImages={canDownloadImages}
                onPreview={(idx, data, total) =>
                  handleClickPreview(idx, data, total, 'supplementary')
                }
                collapseMaxHeight="100%"
                collapseOverflow="hidden"
                removeDeleteButton={removeDeleteButton}
                handDeleteImage={handDeleteImage}
                reloadTrigger={reloadTrigger}
              />
            )}

            {imagesSlide && imagesSlide.length > 0 && (
              <KogModalSlideImages
                images={imagesSlide}
                imageIndex={imageIndex}
                isOpen={isOpenModalFade}
                setIsOpen={setIsOpenModalFade}
                isInModal
                totalImages={totalRecords}
                onLoadMore={
                  hasMoreImages ? handleLoadMoreSlideImages : undefined
                }
                downloadImage={
                  canDownloadImages ? handleDownloadImage : undefined
                }
              />
            )}
          </>
        ) : (
          <LoadingComponent />
        )}
      </S.BoxModalImages>
    </S.Wrapper>
  );
}

function WrapperModalImages(props: ModalImagesProps) {
  const titleModal = `${getTranslateByKey(
    'information.images'
  )} / ${getTranslateByKey('information.files')}`;

  return (
    <KogModalSideComponent
      title={titleModal}
      open={props.isOpenModal}
      closeModal={props.closeModal}>
      {props.isOpenModal && <ModalImages {...props} />}
    </KogModalSideComponent>
  );
}
export default WrapperModalImages;
